import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Callout, ProgressBar, Skeleton, Text } from '../../../../components';
import { cardTiles, hasAboveTheFold, type AboveTheFold as AboveTheFoldData, type CardRedrawOffer } from '../../above-the-fold-api';
import type { AtfCardsRun } from '../../atf-cards-run';
import type { AboveTheFoldStatus } from '../../useAboveTheFold';
import type { CardRedrawBusy } from '../../useAtfCardRedraws';
import { GenerateButton } from '../GenerateButton';
import { LumioGlyph } from '../LumioMark';
import {
  Block,
  BlockHead,
  ComparePair,
  CompareSide,
  HeadAction,
  RedrawButton,
  RedrawCost,
  RedrawLabel,
  RunLine,
  RunNote,
  BlockName,
  Chip,
  GlyphSvg,
  Lead,
  NavIcon,
  NavIcons,
  NavPart,
  Note,
  SharedImage,
  SharedRow,
  SideLabel,
  SkeletonRow,
  Tile,
  TileActions,
  TileArt,
  TileGrid,
  TileImg,
  TileLabel,
  TileNote,
  Wrap,
} from './AboveTheFold.styles';

/** Regenerating the brand's cards: offered only where the cards' record is the brand's own to overwrite. */
export type AtfCardsRegen = {
  /** Credits one regeneration costs (all its images); the button shows it once known. */
  cost?: number;
  /** The regeneration in flight, if any. */
  run: AtfCardsRun | null;
  /** True while the request to queue one is out. */
  queueing: boolean;
  onRegenerate: () => void;
};

/** A card image's new candidate: being drawn, or drawn and waiting to be applied or thrown away. */
export type AtfCardCandidate = {
  /** The image's name in the cards record (the tile it belongs to). */
  fieldName: string;
  status: 'drawing' | 'ready';
  /** The drawn image ("" while it is drawn). */
  candidateUrl: string;
  /** Whole days left to apply it. */
  daysLeft: number;
};

/** Redrawing one card image at a time (charged per image), offered where the brand's hero deployment has the image. */
export type AtfCardRedraw = {
  /** Credits one redraw costs; each Redraw shows it once known. */
  cost?: number;
  /** Each image by its name in the cards record, with what it offers; an image absent here offers nothing. */
  offers: Record<string, CardRedrawOffer>;
  /** The one image whose candidate is being drawn or waits (one at a time: applying one makes any other stale). */
  pending: AtfCardCandidate | null;
  /** The request out, if any: every redraw control holds meanwhile. */
  busy: CardRedrawBusy;
  onRedraw: (fieldName: string) => void;
  onApply: () => void;
  onDiscard: () => void;
};

export type AboveTheFoldProps = {
  status: AboveTheFoldStatus;
  atf: AboveTheFoldData | null;
  onRetry: () => void;
  /** Absent: nothing here can be regenerated (the page has no way to queue it). */
  cardsRegen?: AtfCardsRegen;
  /** Absent: no image can be redrawn on its own here. */
  cardRedraw?: AtfCardRedraw;
};

/** The Above the fold sub-tab's glyph, in `currentColor`: a page whose top band (what shows before any scrolling) is
 *  filled, cut off by a dashed fold line, with the page's content below it. */
export const AboveTheFoldGlyph = ({ size = 15 }: { size?: number }): React.ReactElement => (
  <GlyphSvg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    $size={size}
  >
    <path d="M3.5 6.5a3 3 0 0 1 3-3h11a3 3 0 0 1 3 3V10h-17z" fill="currentColor" fillOpacity={0.3} stroke="none" />
    <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
    <path d="M3.5 10h17" strokeDasharray="2.4 2" />
    <path d="M7.5 14h9M7.5 17h5" />
  </GlyphSvg>
);

/**
 * Website Creatives' "Above the fold" sub-tab: what visitors see first on the brand's home page that is not one of
 * its banners (those are edited under All Promotions & Creatives). The brand's own Casino and Sports cards, and the
 * parts every site on the template shares (read-only). Nothing at all when there is nothing to read with, or nothing
 * of that kind above the home page's sections.
 */
export const AboveTheFold = ({ status, atf, onRetry, cardsRegen, cardRedraw }: AboveTheFoldProps): React.ReactElement | null => {
  const { t } = useTranslation();
  if (status === 'idle') return null;
  if (status === 'loading') {
    return (
      <Wrap aria-busy="true" data-testid="atf-loading">
        <Lead text="What visitors see first on your home page, before its sections." />
        <SkeletonRow>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} block height={150} radius={12} />
          ))}
        </SkeletonRow>
      </Wrap>
    );
  }
  if (status === 'failed') {
    return (
      <Wrap>
        <Callout
          tone="danger"
          title={t("We couldn't load what your home page shows first")}
          text={t('Please try again.')}
          action={<Button variant="outline" icon="refresh" label={t('Try again')} onClick={onRetry} />}
        />
      </Wrap>
    );
  }
  if (!atf || !hasAboveTheFold(atf)) return null;

  const tiles = atf.cards ? cardTiles(atf.cards) : [];
  // Only a record the brand owns is overwritten by a regeneration; the service refuses any other.
  const regen = atf.cards?.editable ? cardsRegen : undefined;
  const run = regen?.run ?? null;
  const redraw = atf.cards?.editable ? cardRedraw : undefined;
  const candidate = redraw?.pending ?? null;
  // No image is redrawn while a request is out, while another image's candidate waits (applying one makes the others
  // stale), or while both cards are being regenerated (their new art would too). Regenerate waits for a candidate too:
  // the service refuses it while one is drawing or waits to be applied or discarded.
  const redrawHeld = !redraw || redraw.busy !== null || candidate !== null || run !== null;
  const cardName = (card: 'casino' | 'sport' | 'other', name: string): string => {
    if (card === 'casino') return atf.cards?.casino || t('Casino');
    if (card === 'sport') return atf.cards?.sport || t('Sports');
    return name;
  };

  return (
    <Wrap>
      <Lead text="What visitors see first on your home page, before its sections." />

      {atf.cards ? (
        <Block data-testid="atf-cards">
          <BlockHead>
            <BlockName text="Category cards" />
            {regen ? (
              <HeadAction>
                <GenerateButton
                  label={t('Regenerate')}
                  cost={regen.cost}
                  compact
                  secondary
                  disabled={regen.queueing || run !== null || candidate !== null}
                  onClick={regen.onRegenerate}
                />
              </HeadAction>
            ) : null}
          </BlockHead>
          {atf.cards.title ? <Note text={atf.cards.title} /> : null}
          {run ? (
            <RunLine role="status" data-testid="atf-cards-run">
              <RunNote text="Regenerating your cards: {{done}} of {{total}} images" placeholders={{ done: run.completed, total: run.total }} />
              <ProgressBar value={run.total > 0 ? Math.round((run.completed / run.total) * 100) : 0} animate />
            </RunLine>
          ) : null}
          {candidate?.status === 'ready' ? <Note text="Finish or discard the waiting redraw first." /> : null}
          <TileGrid $busy={run !== null}>
            {tiles.map((tile) => {
              const label = `${cardName(tile.card, tile.name)} · ${tile.cutout ? t('Cut-out') : t('Square')}`;
              const offer = redraw?.offers[tile.name];
              const mine = candidate?.fieldName === tile.name ? candidate : null;
              const current = (
                <TileArt $aspect="1 / 1">
                  <TileImg src={tile.url} alt={label} loading="lazy" />
                </TileArt>
              );
              return (
                <Tile key={tile.name} $wide={mine !== null} data-testid={`atf-tile-${tile.name}`}>
                  {mine ? (
                    <ComparePair>
                      <CompareSide>
                        {current}
                        <SideLabel text="Current" />
                      </CompareSide>
                      <CompareSide>
                        <TileArt $aspect="1 / 1">
                          {mine.status === 'ready' && mine.candidateUrl ? (
                            <TileImg src={mine.candidateUrl} alt={t('New {{name}}', { name: label })} />
                          ) : (
                            <Skeleton block height="100%" radius={12} />
                          )}
                        </TileArt>
                        <SideLabel text="New" />
                      </CompareSide>
                    </ComparePair>
                  ) : (
                    current
                  )}
                  <TileLabel text={label} />
                  {mine && redraw ? (
                    mine.status === 'ready' ? (
                      <>
                        <TileActions>
                          <Button label={t('Apply')} onClick={redraw.onApply} disabled={redraw.busy !== null} />
                          <Button variant="outline" label={t('Discard')} onClick={redraw.onDiscard} disabled={redraw.busy !== null} />
                        </TileActions>
                        {mine.daysLeft === 1 ? (
                          <TileNote text="Expires in {{n}} day" placeholders={{ n: mine.daysLeft }} />
                        ) : (
                          <TileNote text="Expires in {{n}} days" placeholders={{ n: mine.daysLeft }} />
                        )}
                      </>
                    ) : (
                      <TileNote text="Drawing a new image…" />
                    )
                  ) : offer?.kind === 'redrawable' && redraw ? (
                    <RedrawButton type="button" disabled={redrawHeld} onClick={() => redraw.onRedraw(tile.name)}>
                      <RedrawLabel text="Redraw" />
                      {redraw.cost != null ? (
                        <RedrawCost>
                          <LumioGlyph size={13} />
                          {redraw.cost === 1 ? (
                            <Text text="{{n}} credit" placeholders={{ n: redraw.cost }} />
                          ) : (
                            <Text text="{{n}} credits" placeholders={{ n: redraw.cost }} />
                          )}
                        </RedrawCost>
                      ) : (
                        <LumioGlyph size={13} />
                      )}
                    </RedrawButton>
                  ) : offer?.kind === 'blocked' ? (
                    offer.reason ? (
                      <TileNote text={offer.reason} />
                    ) : (
                      <TileNote text="This image can't be redrawn right now." />
                    )
                  ) : null}
                </Tile>
              );
            })}
          </TileGrid>
        </Block>
      ) : null}

      {atf.shared.length ? (
        <Block data-testid="atf-shared">
          <BlockHead>
            <BlockName text="Shared with other sites" />
            <Chip text="Read-only" />
          </BlockHead>
          <Note text="These come from your site's template and show on every site built on it, so they can't be changed for one brand." />
          <SharedRow>
            {atf.shared.map((part) => {
              if (part.kind === 'image') {
                return (
                  <SharedImage key={part.key}>
                    <TileArt $aspect="2 / 1">
                      <TileImg $cover src={part.url} alt={part.alt || part.name} loading="lazy" />
                    </TileArt>
                    <TileLabel text={part.name} />
                  </SharedImage>
                );
              }
              if (part.kind === 'navigation') {
                return (
                  <NavPart key={part.key}>
                    <NavIcons aria-label={part.name}>
                      {part.links
                        .filter((link) => link.icon)
                        .map((link) => (
                          <NavIcon key={link.id || link.name} title={link.name}>
                            <img src={link.icon} alt={link.name} loading="lazy" />
                          </NavIcon>
                        ))}
                    </NavIcons>
                    <TileLabel text={part.name} />
                  </NavPart>
                );
              }
              return <TileLabel key={part.key} text={part.name} />;
            })}
          </SharedRow>
        </Block>
      ) : null}
    </Wrap>
  );
};
